import type { DomainRecord, DomainSchema } from './types';
import type { PriorityDomainName } from './registry';
import { PRIORITY_DOMAIN_REGISTRY } from './registry';

// ─── Card title resolution ────────────────────────────────────────────────────

/**
 * Resolves the card heading for a domain record using the domain's `cardTitle`
 * template. Returns `null` when:
 *  - the schema has `cardTitle: null`, or
 *  - every `{fieldName}` placeholder resolves to an empty / missing value.
 *
 * Literal separators that end up adjacent to an empty placeholder are stripped,
 * so "{title} at {companyName}" with no companyName becomes "Software Engineer"
 * rather than "Software Engineer at ".
 *
 * @example
 * resolveCardTitle(POSITIONS, { title: 'Engineer', companyName: 'Acme' })
 * // → "Engineer at Acme"
 *
 * resolveCardTitle(POSITIONS, { title: 'Engineer' })
 * // → "Engineer"
 *
 * resolveCardTitle(PHONE_NUMBERS, { number: '07700900000' })
 * // → null  (cardTitle is null for this domain)
 */
export function resolveCardTitle(schema: DomainSchema, record: DomainRecord): string | null {
  if (schema.cardTitle === null) return null;

  // Tokenise: alternating literal and {field} segments
  const raw = schema.cardTitle;
  const tokens = raw.split(/(\{[^}]+\})/g);

  type Token =
    | { kind: 'literal'; value: string }
    | { kind: 'field'; value: string; resolved: string; empty: boolean };

  const parts: Token[] = tokens.map(t => {
    if (t.startsWith('{') && t.endsWith('}')) {
      const field = t.slice(1, -1);
      const val = record[field];
      const str = val != null && val !== '' ? String(val) : '';
      return { kind: 'field', value: field, resolved: str, empty: str === '' };
    }
    return { kind: 'literal', value: t };
  });

  // Remove literal separators that sit next to empty field tokens
  const kept: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p.kind === 'field') {
      if (!p.empty) kept.push(p.resolved);
      // empty fields are silently dropped
      continue;
    }
    // Literal: keep only if both neighbours (if any field token) are non-empty
    const prev = parts
      .slice(0, i)
      .reverse()
      .find(t => t.kind === 'field') as Extract<Token, { kind: 'field' }> | undefined;
    const next = parts.slice(i + 1).find(t => t.kind === 'field') as
      | Extract<Token, { kind: 'field' }>
      | undefined;
    const prevEmpty = prev ? prev.empty : false;
    const nextEmpty = next ? next.empty : false;
    if (!prevEmpty && !nextEmpty) kept.push(p.value);
  }

  const result = kept.join('').trim();
  return result || null;
}

// ─── Label formatting ─────────────────────────────────────────────────────────

/**
 * Returns the human-readable label for a field. Uses the explicit `label`
 * from the schema if present; otherwise converts camelCase to "Title Case".
 *
 * @example
 * getFieldLabel(POSITIONS.fields.companyName)  // → "Company"  (explicit)
 * getFieldLabel(POSITIONS.fields.location)     // → "Location" (derived)
 */
export function getFieldLabel(
  fieldDef: { type: string; label?: string },
  fieldName: string
): string {
  if (fieldDef.label) return fieldDef.label;
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

// ─── Schema lookup ────────────────────────────────────────────────────────────

/**
 * Returns the `DomainSchema` for a priority domain by name, or `undefined`
 * if the domain is not in the priority registry.
 */
export function getPrioritySchema(
  domain: string
): (typeof PRIORITY_DOMAIN_REGISTRY)[PriorityDomainName] | undefined {
  return PRIORITY_DOMAIN_REGISTRY[domain as PriorityDomainName];
}

/**
 * Returns the ordered visible field names for a priority domain — i.e. all
 * fields extracted from the layout rows, in row order.
 * Hidden fields and fields used only in `cardTitle` are excluded.
 */
export function getLayoutFieldNames(domain: string): string[] {
  const schema = getPrioritySchema(domain);
  if (!schema) return [];
  return schema.layout.flatMap(row => row.map(cell => cell.field));
}
